import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  ShieldCheck, 
  Sparkles, 
  Repeat, 
  Coins, 
  ArrowRight, 
  CheckCircle2, 
  FileText,
  Package,
  Facebook,
  ArrowRightLeft
} from 'lucide-react';
import SEO from '../components/common/SEO';
import { motion } from 'framer-motion';

export default function WelcomePage() {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const handleAccept = () => {
    localStorage.setItem('hasAcceptedTerms', 'true');
    localStorage.setItem('hasSeenWelcome', 'true');
    localStorage.setItem('hideTourV3', 'true');
    sessionStorage.setItem('hideRegisterPopup', 'true');
    navigate('/');
  };

  const isHe = language === 'he';
  const brandName = isHe ? 'Ahlafot' : 'SwapX';

  return (
    <>
      <SEO 
        title={isHe ? 'ברוכים הבאים ל-Ahlafot' : 'Welcome to SwapX'}
        description={isHe ? 'הצטרפו לפלטפורמת ההחלפות המובילה. החלף פריטים בביטחון ובקלות.' : 'Join the premier bartering marketplace. Trade items safely and easily.'}
        canonical="https://ahlafot.co.il/welcome"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-background via-muted/30 to-background py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-12">
          
          {/* Hero Welcome Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-4 pt-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20 text-xs font-black uppercase tracking-widest animate-pulse">
              <Sparkles size={14} />
              <span>{isHe ? 'פלטפורמת ההחלפות החכמה' : 'The Smart Bartering Marketplace'}</span>
            </div>
            
            <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-foreground bg-clip-text text-transparent bg-gradient-to-r from-primary via-purple-600 to-indigo-600 drop-shadow-xs">
              {isHe ? `ברוכים הבאים ל-${brandName}` : `Welcome to ${brandName}`}
            </h1>
            
            <p className="text-base sm:text-xl text-muted-foreground font-medium max-w-3xl mx-auto leading-relaxed">
              {isHe 
                ? 'המקום שבו כל פריט ישן הופך להזדמנות חדשה. החליפו מוצרים ושירותים, חסכו כסף ותיהנו מסחר הוגן ובטוח.' 
                : 'Where unused items find new life. Trade products, save money, and enjoy a fair, secure bartering community.'}
            </p>
          </motion.div>

          {/* Core Platform Pillars Grid */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {/* Feature 1 */}
            <div className="p-6 rounded-3xl bg-card/80 backdrop-blur-md border border-border/60 shadow-lg space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/15 text-purple-500 flex items-center justify-center border border-purple-500/20 shadow-xs">
                <Repeat size={24} />
              </div>
              <h3 className="text-lg font-black text-foreground">{isHe ? 'החלפות חכמות 🔄' : 'Smart Bartering 🔄'}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {isHe 
                  ? 'מצאו אנשים שמחפשים את מה שיש לכם ומציעים בדיוק את מה שאתם צריכים.' 
                  : 'Connect with people looking for what you have and offering what you need.'}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="p-6 rounded-3xl bg-card/80 backdrop-blur-md border border-border/60 shadow-lg space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 text-emerald-500 flex items-center justify-center border border-emerald-500/20 shadow-xs">
                <Coins size={24} />
              </div>
              <h3 className="text-lg font-black text-foreground">{isHe ? 'איזון כספי גמיש 💰' : 'Cash Balance Flexibility 💰'}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {isHe 
                  ? 'ניתן להוסיף או לבקש כסף כדי להשלים פערים בשווי הפריטים באופן הוגן.' 
                  : 'Add or request cash to balance item value differences fairly and easily.'}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="p-6 rounded-3xl bg-card/80 backdrop-blur-md border border-border/60 shadow-lg space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/15 text-blue-500 flex items-center justify-center border border-blue-500/20 shadow-xs">
                <ShieldCheck size={24} />
              </div>
              <h3 className="text-lg font-black text-foreground">{isHe ? 'קהילה בטוחה 🛡️' : 'Verified Community 🛡️'}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {isHe 
                  ? 'חדר משא ומתן מאובטח, אימות משתמשים ודירוגי אמינות לשמירה על סחר הוגן.' 
                  : 'Secure negotiation rooms, user verification, and trust ratings.'}
              </p>
            </div>
          </motion.div>

          {/* Comprehensive Platform Tour / Step-by-Step Showcase */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="rounded-3xl bg-card/90 border border-border p-6 sm:p-8 shadow-xl space-y-8"
          >
            <div className="text-center space-y-2 border-b border-border/50 pb-6">
              <h2 className="text-2xl sm:text-3xl font-black text-foreground">
                {isHe ? 'איך הכל עובד ב-Ahlafot?' : `How Everything Works on ${brandName}`}
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground font-medium">
                {isHe 
                  ? 'כל הכלים והאפשרויות שעומדים לרשותכם בפלטפורמה' 
                  : 'All the tools and features designed to make your trading seamless'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Tour Feature 1 */}
              <div className="flex items-start gap-4 p-4 rounded-2xl bg-muted/30 border border-border/40">
                <div className="p-3 rounded-2xl bg-primary/10 text-primary border border-primary/20 shrink-0">
                  <Package size={22} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-black text-foreground">{isHe ? '1. גילוי פריטים ומציאות 📦' : '1. Discover Items & Treasures 📦'}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {isHe 
                      ? 'דפדפו באלפי פריטים – מכוניות, אלקטרוניקה, שעונים, רהיטים ופריטי אספנות מכל הארץ.' 
                      : 'Explore thousands of listings—cars, electronics, luxury watches, furniture, and collectibles.'}
                  </p>
                </div>
              </div>

              {/* Tour Feature 2 */}
              <div className="flex items-start gap-4 p-4 rounded-2xl bg-muted/30 border border-border/40">
                <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 shrink-0">
                  <Facebook size={22} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-black text-foreground">{isHe ? '2. יבוא בלחיצת כפתור ⚡️' : '2. Instant Marketplace Import ⚡️'}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {isHe 
                      ? 'כבר יש לכם מודעות בפייסבוק מרקטפלייס? תוכלו לייבא אותן ישירות למערכת בשניות!' 
                      : 'Already have Facebook Marketplace listings? Import them directly into the platform in 1-click!'}
                  </p>
                </div>
              </div>

              {/* Tour Feature 3 */}
              <div className="flex items-start gap-4 p-4 rounded-2xl bg-muted/30 border border-border/40">
                <div className="p-3 rounded-2xl bg-indigo-500/10 text-indigo-500 border border-indigo-500/20 shrink-0">
                  <ArrowRightLeft size={22} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-black text-foreground">{isHe ? '3. חדר משא ומתן והצעות 🤝' : '3. Negotiation Room & Offers 🤝'}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {isHe 
                      ? 'שלחו הצעת החלפה, צרפו פריטים מהמלאי שלכם, שוחחו בצ׳אט חי וסגרו עסקאות.' 
                      : 'Send trade offers, include inventory items, chat in real-time, and agree on deal terms.'}
                  </p>
                </div>
              </div>

              {/* Tour Feature 4 */}
              <div className="flex items-start gap-4 p-4 rounded-2xl bg-muted/30 border border-border/40">
                <div className="p-3 rounded-2xl bg-amber-500/10 text-amber-500 border border-amber-500/20 shrink-0">
                  <Coins size={22} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-black text-foreground">{isHe ? '4. הקפצת מודעות במטבעות 💰' : '4. Coin Listing Boosts 💰'}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {isHe 
                      ? 'השתמשו במטבעות יומיים כדי להבליט את המודעה שלכם ולקבל פי 10 יותר הצעות!' 
                      : 'Use daily coins to boost your listing to the top of search and get up to 10x more offers!'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Terms & Conditions Agreement Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="rounded-3xl bg-card/90 border border-border p-6 sm:p-8 shadow-xl space-y-6"
          >
            <div className="flex items-center gap-3 border-b border-border/50 pb-4">
              <div className="p-2.5 rounded-2xl bg-primary/10 text-primary border border-primary/20">
                <FileText size={22} />
              </div>
              <div>
                <h2 className="text-xl font-black text-foreground">
                  {isHe ? 'תנאי השימוש וכללי הקהילה' : 'Terms of Service & Community Guidelines'}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {isHe ? 'אנא קרא ואשר את תנאי השימוש כדי להמשיך לגלישה באתר' : 'Please review and accept the terms of service to enter the platform'}
                </p>
              </div>
            </div>

            {/* Scrollable Lawyer-Grade Terms Content */}
            <div className="h-80 overflow-y-auto pr-3 space-y-5 text-xs text-muted-foreground leading-relaxed rounded-2xl bg-muted/30 p-4 sm:p-6 border border-border/40 font-medium">
              
              <div>
                <h4 className="font-black text-foreground text-sm mb-1">
                  1. {isHe ? 'הסכם משפטי מחייב והסכמה לתנאים' : '1. Binding Legal Agreement'}
                </h4>
                <p>
                  {isHe 
                    ? `שימוש, גלישה או רישום לפלטפורמת ${brandName} מהווים הסכם משפטי מפורש, סופי ומחייב בין המשתמש לבין הנהלת הפלטפורמה. במידה ואינך מסכים לתנאים אלו במלואם, אינך מורשה להשתמש או לפרסם באתר.`
                    : `Accessing or using the ${brandName} platform constitutes a legally binding contract between you ("User") and the platform owners. If you do not agree unconditionally, you are prohibited from accessing the service.`}
                </p>
              </div>

              <div>
                <h4 className="font-black text-foreground text-sm mb-1">
                  2. {isHe ? 'מעמד הפלטפורמה: פטור מוחלט מאחריות כמתווך טכנולוגי בלבד' : '2. Absolute Immunity & Intermediary Safe Harbor'}
                </h4>
                <p>
                  {isHe 
                    ? `פלטפורמת ${brandName} פועלת אך ורק כזירת תוכנה טכנולוגית פסיבית המקשרת בין משתמשים עצמאיים. הפלטפורמה, הנהלתה, מקימיה ומפתחיה אינם צד לכל עסקה, מו״מ, בדיקה פיזית, החלפה או העברת כספים. הנהלת האתר אינה בודקת, מפקחת, מאמתת, ערבה או מייצגת את תקינותם, מקוריותם, בטיחותם, חוקיותם, בעלותם או שוויים של פריטים המוצגים באתר. כל העסקאות והמפגשים הפיזיים מבוצעים באחריותם הבלעדית והמוחלטת של המשתמשים בלבד.`
                    : `${brandName} operates strictly as an intermediary online communications platform. Platform owners, operators, and developers are not parties to any trade, inspection, payment, or exchange. We do not inspect, test, verify, endorse, or guarantee the condition, authenticity, ownership, safety, or legality of listed items. All interactions and trades are conducted strictly at the independent risk of users.`}
                </p>
              </div>

              <div>
                <h4 className="font-black text-foreground text-sm mb-1">
                  3. {isHe ? 'איסור מוחלט על תוכן פוגעני, פורנוגרפיה ומוצרים אסורים' : '3. Zero-Tolerance Policy: Prohibited Content & Adult Media'}
                </h4>
                <p>
                  {isHe 
                    ? 'חלה איסור מוחלט על פרסום תכנים הכוללים: פורנוגרפיה, תכנים מיניים, אלימות, דברי שטנה, הטרדה, מוצרים מזויפים, הפרת סימני מסחר או זכויות יוצרים, נשק, חומרים מסוכנים, או כל תוכן העומד בניגוד לחוק. כל מודעה עוברת מנגנון אישור מנהל (Admin Review) לפני פרסומה לציבור. הנהלת האתר שומרת לעצמה את הזכות הבלעדית למחוק כל מודעה, לחסום חשבונות משתמשים, ולדווח לרשויות החוק ללא התראה מוקדמת.'
                    : 'We enforce a strict zero-tolerance policy against unlawful, fraudulent, defamatory, sexually explicit, pornographic, violent, counterfeit, weapon-related, or trademark-infringing content. All listings undergo mandatory administrative moderation before public display. Administrators reserve the absolute right to remove content, ban user accounts, and notify law enforcement agencies.'}
                </p>
              </div>

              <div>
                <h4 className="font-black text-foreground text-sm mb-1">
                  4. {isHe ? 'אחריות המשתמש על תוכן וזכויות יוצרים' : '4. User Content Warranty & Intellectual Property'}
                </h4>
                <p>
                  {isHe 
                    ? 'המשתמש מצהיר ומתחייב כי הוא הבעלים החוקי הבלעדי או בעל ההרשאה המפורשת לכל תמונה, טקסט או מידע שהוא מעלה או מייבא למערכת (לרבות ייבוא מפייסבוק מרקטפלייס). פרסום תוכן של צד שלישי ללא אישור מהווה הפרת חוק חמורה והמשתמש נושא באחריות המשפטית והפלילית הבלעדית לכך.'
                    : 'You represent and warrant that you possess full legal ownership or authorization for all images, descriptions, and data uploaded or imported to the platform (including Facebook Marketplace imports). Uploading third-party copyrighted media without written consent is strictly illegal.'}
                </p>
              </div>

              <div>
                <h4 className="font-black text-foreground text-sm mb-1">
                  5. {isHe ? 'הגבלת אחריות מוחלטת ואיסור הגשת תביעות' : '5. Comprehensive Release & Limitation of Liability'}
                </h4>
                <p>
                  {isHe 
                    ? `במידה המרבית המותרת על פי דין, המשתמש מוותר באופן מלא, סופי, מוחלט ובלתי חוזר על כל טענה, דרישה, תביעה או עילת תביעה כנגד הנהלת הפלטפורמה, מקימיה, מפעילה, בעליה ומפתחיה, בגין כל נזק (ישיר, עקיף, מקרי, תוצאתי או עונשי), הפסד כספי, עוגמת נפש, פגיעה גופנית, נזק לרכוש, מעשי תרמית, או מחלוקות הנובעים משימוש בפלטפורמה, ממודעות שנצפו, או ממפגשים ועסקאות שנערכו בין משתמשים.`
                    : `To the maximum extent permitted by law, you explicitly, irrevocably, and forever release, waive, and discharge the platform owners, operators, developers, and affiliates from any and all claims, liabilities, lawsuits, damages (direct, indirect, consequential, punitive, or special), financial losses, personal injury, property damage, or disputes arising out of your use of the platform.`}
                </p>
              </div>

              <div>
                <h4 className="font-black text-foreground text-sm mb-1">
                  6. {isHe ? 'שיפוי משפטי מלא של הנהלת האתר' : '6. Full Legal Indemnification'}
                </h4>
                <p>
                  {isHe 
                    ? 'המשתמש מתחייב לפצות, לשפות וללהגן על הנהלת האתר, מפעילה ומפתחיה מפני כל תביעה, דרישה, נזק, הפסד, חבות, קנס, או הוצאה משפטית (לרבות שכר טרחת עורכי דין) שייגרמו עקב פרסום מודעות ע״י המשתמש, הפרת תנאי שימוש אלו, הפרת זכויות של צד שלישי, או כל מעשה או מחדל מצידו.'
                    : 'You agree to fully defend, indemnify, and hold harmless the platform owner, operators, and developers from and against any third-party claims, lawsuits, damages, losses, liabilities, penalties, and legal fees (including attorney costs) arising from your listings, content, breaches of these terms, or violations of third-party rights.'}
                </p>
              </div>

              <div>
                <h4 className="font-black text-foreground text-sm mb-1">
                  7. {isHe ? 'סמכות שיפוט בלעדית ודין חל' : '7. Governing Law & Exclusive Jurisdiction'}
                </h4>
                <p>
                  {isHe 
                    ? 'על תנאים אלו ועל כל נושא הנובע מהשימוש בפלטפורמה יחולו אך ורק דיני מדינת ישראל, וסמכות השיפוט הבלעדית והייחודית בכל מחלוקת נתונה לבתי המשפט המוסמכים במחוז תל אביב, תוך ויתור מפורש על זכות להשתתף בתובענה ייצוגית.'
                    : 'These Terms shall be governed exclusively by applicable laws. Any legal dispute shall be subject to the exclusive jurisdiction of the competent courts of Tel Aviv, Israel. You explicitly waive any right to participate in class action lawsuits.'}
                </p>
              </div>

            </div>

            {/* Acceptance Action Footer */}
            <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border/50">
              <div className="flex items-center gap-2 text-xs font-bold text-emerald-500">
                <CheckCircle2 size={18} />
                <span>{isHe ? 'קראתי ואני מאשר את תנאי השימוש' : 'I have read and agree to the Terms of Service'}</span>
              </div>

              <button
                onClick={handleAccept}
                className="w-full sm:w-auto px-10 py-4 rounded-2xl bg-gradient-to-r from-primary via-purple-600 to-indigo-600 text-white font-black text-base shadow-xl shadow-primary/25 hover:shadow-2xl hover:shadow-primary/40 hover:scale-105 active:scale-95 transition-all duration-300 flex items-center justify-center gap-3 cursor-pointer"
              >
                <span>{isHe ? 'הבנתי, בוא נתחיל!' : 'Accept & Continue to Marketplace'}</span>
                <ArrowRight size={20} />
              </button>
            </div>

          </motion.div>

        </div>
      </div>
    </>
  );
}
